import { $isLinkNode } from "@lexical/link";
import { $isListItemNode, $isListNode, ListItemNode, ListNode } from "@lexical/list";
import { TextNode } from "lexical";
import { notEmpty } from "../../../../../utils/not-empty";
import { NoReplace } from "./markdown-element-transformers";

import type { ElementTransformer, TextMatchTransformer } from "@lexical/markdown";
import type { ElementNode, LexicalNode } from "lexical";

const LIST_INDENT_SIZE = 5;

const listExport = (
  listNode: ListNode,
  exportChildren: (node: ElementNode) => string,
  depth: number,
  bulletListPrefix: string,
): string =>
  listNode
    .getChildren()
    .map((listItemNode, index) => {
      if (!$isListItemNode(listItemNode)) return;

      if (listItemNode.getChildrenSize() === 1) {
        const firstChild = listItemNode.getFirstChild();
        if ($isListNode(firstChild)) {
          return listExport(firstChild, exportChildren, depth + 1, bulletListPrefix);
        }
      }

      const prefix =
        listNode.getListType() === "number" ? `${listNode.getStart() + index}. ` : bulletListPrefix;

      return " ".repeat(depth * LIST_INDENT_SIZE) + prefix + exportChildren(listItemNode);
    })
    .filter(notEmpty)
    .join("\n");

export const LIST = (bulletListPrefix: string): ElementTransformer => ({
  dependencies: [ListNode, ListItemNode],
  ...NoReplace,
  export: (node, exportChildren) =>
    $isListNode(node)
      ? JSON.stringify({
          type: "paragraph",
          text: listExport(node, exportChildren, 0, bulletListPrefix),
        })
      : null,
  type: "element",
});

export const linkNode = (format: (text: string, url: string) => string): TextMatchTransformer => ({
  type: "text-match",
  ...NoReplace,
  dependencies: [TextNode],
  export(node: LexicalNode, exportChildren) {
    if (!$isLinkNode(node)) return null;

    return format(exportChildren(node) ?? "", node.getURL());
  },
});
